// HTTP client for the FastAPI backend (docs/api-contract.md).
//
// - Base URL comes from VITE_API_URL (default http://localhost:8000).
// - Paths are absolute from the origin, e.g. '/api/v1/products' or '/media/...'.
// - Errors are RFC 9457 problem+json, parsed into ApiError.
// - Network failures (server unreachable, DNS, CORS) become ApiError with code 'network'.

const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:8000').replace(/\/+$/, '')

export { API_BASE_URL }

export class ApiError extends Error {
  constructor({ status = 0, code = 'config', title = 'Error', detail = null, errors = null } = {}) {
    super(detail || title)
    this.name = 'ApiError'
    this.status = status // HTTP status; 0 for network failures
    this.code = code // auth | forbidden | not_found | invalid | conflict | config | network
    this.title = title
    this.detail = detail
    this.errors = errors // 422 only: [{field, message}, ...] — null otherwise
  }
}

// Fallback mapping when a non-2xx response is not problem+json (proxy errors etc.)
const STATUS_TO_CODE = {
  400: 'invalid',
  401: 'auth',
  403: 'forbidden',
  404: 'not_found',
  409: 'conflict',
  422: 'invalid',
}

export function buildQuery(params) {
  if (!params) return ''
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null) continue
        search.append(key, String(item))
      }
    } else {
      search.append(key, String(value))
    }
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

export async function apiFetch(path, { method = 'GET', params, body, getToken, signal, headers: extraHeaders } = {}) {
  const url = `${API_BASE_URL}${path}${buildQuery(params)}`
  const headers = { Accept: 'application/json', ...extraHeaders }

  if (getToken) {
    let token = null
    try {
      token = await getToken()
    } catch {
      // signed out / Clerk not ready — proceed unauthenticated
    }
    if (token) headers.Authorization = `Bearer ${token}`
  }

  const init = { method, headers, signal }
  if (body !== undefined) {
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      init.body = body // browser sets multipart Content-Type with boundary
    } else {
      headers['Content-Type'] = 'application/json'
      init.body = JSON.stringify(body)
    }
  }

  let res
  try {
    res = await fetch(url, init)
  } catch (err) {
    if (err?.name === 'AbortError') throw err
    throw new ApiError({
      status: 0,
      code: 'network',
      title: 'Network error',
      detail: err?.message || 'Could not reach the server',
    })
  }

  if (!res.ok) {
    throw await parseErrorResponse(res)
  }
  if (res.status === 204) return null

  const contentType = res.headers.get('content-type') || ''
  if (!contentType.includes('json')) return null
  return res.json()
}

async function parseErrorResponse(res) {
  const contentType = res.headers.get('content-type') || ''
  if (contentType.includes('application/problem+json') || contentType.includes('application/json')) {
    let problem = null
    try {
      problem = await res.json()
    } catch {
      // fall through to status-based error
    }
    if (problem && typeof problem === 'object') {
      return new ApiError({
        status: typeof problem.status === 'number' ? problem.status : res.status,
        code: problem.code || STATUS_TO_CODE[res.status] || 'config',
        title: problem.title || res.statusText || 'Request failed',
        detail: problem.detail ?? null,
        errors: Array.isArray(problem.errors) ? problem.errors : null,
      })
    }
  }
  return new ApiError({
    status: res.status,
    code: STATUS_TO_CODE[res.status] || 'config',
    title: res.statusText || 'Request failed',
    detail: null,
  })
}

// Builds a display URL for a media path field from the API (image_path, file_path, ...).
export function mediaUrl(path) {
  if (!path) return null
  return `${API_BASE_URL}/media/${String(path).replace(/^\/+/, '')}`
}

// Convenience client bound to a token getter. `getToken` is called fresh per request
// (hold it in a ref upstream so the latest Clerk getToken is always used).
export function createApiClient(getToken) {
  const request = (path, opts = {}) => apiFetch(path, { getToken, ...opts })
  return {
    request,
    get: (path, params, opts = {}) => request(path, { ...opts, method: 'GET', params }),
    post: (path, body, opts = {}) => request(path, { ...opts, method: 'POST', body }),
    patch: (path, body, opts = {}) => request(path, { ...opts, method: 'PATCH', body }),
    put: (path, body, opts = {}) => request(path, { ...opts, method: 'PUT', body }),
    del: (path, opts = {}) => request(path, { ...opts, method: 'DELETE' }),
    getToken,
  }
}
